import { create } from 'zustand'
import { api } from '../lib/api'

/**
 * Paleta de comandos do campo de digitar (etapa 1, R7-R10). Um comando de
 * barra ("/algo") é isto: `id` é o que casa contra o que a pessoa digitou
 * depois da barra, `prompt` é o texto que o campo recebe (e que efetivamente
 * viaja como mensagem) quando o comando é escolhido, `label`/`description`
 * são só para exibição na lista, `order` decide a posição quando o filtro
 * empata.
 */
export interface CommandEntry {
  id: string
  label: string
  description?: string
  prompt: string
  order: number
  /**
   * `false` = comando LOCAL, sem skill no catálogo do motor: a paleta NÃO pode
   * marcar o turno com `command:<id>` (o motor responde 400 para id
   * desconhecido). Ausente/`true` = comportamento antigo. Ver
   * `useCommandPalette.ts` (comando `/cronograma`, STO-530/F7).
   */
  arm?: boolean
}

/**
 * Forma de fio, deliberadamente permissiva.
 *
 * A porta boa é `GET /api/v1/chat/commands`, publicada pelo motor nesta MESMA
 * entrega: devolve `{ commands: [{ id, label, description, prompt, order }] }`
 * já no idioma pedido em `?locale=`.
 *
 * O mapeamento continua aceitando TAMBÉM a forma antiga
 * (`{ skills: [{ name, description }] }`, do catálogo de onboarding) por um
 * motivo só: tela e motor sobem separados. Entre um deploy e outro a tela nova
 * pode falar com um motor velho, e cair para `name`/`description` é melhor do
 * que a paleta aparecer vazia. Se a porta nova responder, ela ganha.
 */
interface CommandsCatalogWireEntry {
  id?: string
  name?: string
  label?: string
  description?: string
  prompt?: string
  order?: number
}

interface CommandsCatalogResponse {
  commands?: CommandsCatalogWireEntry[]
  skills?: CommandsCatalogWireEntry[]
}

export type CommandsStatus = 'idle' | 'loading' | 'ready' | 'error'

interface CommandsState {
  commands: CommandEntry[]
  status: CommandsStatus
  fetchedLocale: string | null
  /**
   * R7 — busca uma vez por sessão e na troca de idioma. Idempotente: uma
   * busca já concluída (sucesso OU falha) para o MESMO idioma não dispara
   * outra chamada de rede.
   */
  fetchCommands: (locale: string) => Promise<void>
}

function toCommandEntry(raw: CommandsCatalogWireEntry, index: number): CommandEntry | null {
  const id = raw.id ?? raw.name
  if (typeof id !== 'string' || id.trim() === '') return null
  const description = typeof raw.description === 'string' ? raw.description : undefined
  const prompt = typeof raw.prompt === 'string' ? raw.prompt : (description ?? id)
  const label = typeof raw.label === 'string' ? raw.label : id
  const order = typeof raw.order === 'number' ? raw.order : index
  return { id, label, description, prompt, order }
}

/** Pura — testável isolada do store e da rede. */
export function mapCommandsCatalogResponse(payload: CommandsCatalogResponse): CommandEntry[] {
  const source = payload.commands ?? payload.skills ?? []
  return source
    .map((raw, index) => toCommandEntry(raw, index))
    .filter((c): c is CommandEntry => c !== null)
}

const COMMANDS_ENDPOINT = '/api/v1/chat/commands'

export const useCommandsStore = create<CommandsState>((set, get) => ({
  commands: [],
  status: 'idle',
  fetchedLocale: null,

  fetchCommands: async (locale: string) => {
    const state = get()
    const alreadySettledForLocale =
      (state.status === 'ready' || state.status === 'error') && state.fetchedLocale === locale
    const alreadyLoadingForLocale = state.status === 'loading' && state.fetchedLocale === locale
    if (alreadySettledForLocale || alreadyLoadingForLocale) return

    set({ status: 'loading', fetchedLocale: locale })
    try {
      const res = await api.get<CommandsCatalogResponse>(
        `${COMMANDS_ENDPOINT}?locale=${encodeURIComponent(locale)}`,
      )
      set({ commands: mapCommandsCatalogResponse(res), status: 'ready', fetchedLocale: locale })
    } catch {
      // R7 — falha na busca NUNCA trava o envio de texto livre. Quem lê
      // `status === 'error'` (useCommandPalette) mostra a linha desabilitada;
      // o campo continua aceitando texto normalmente porque o envio de texto
      // não depende deste store.
      set({ commands: [], status: 'error', fetchedLocale: locale })
    }
  },
}))
